// BGD Engineering Motor Sizing Tables
// For 480V and 240V Three-Phase 60Hz Motors
// These values meet or exceed NEC requirements
//
// SOURCE: the BGD motor sizing standard (bgd-motor-sizing skill) carries the
// 480 V table for 0.5-500 HP and the 240 V table for 0.5-75 HP only. Every
// other row in this file (240 V above 75 HP and the whole 208 V table) is NOT
// a BGD table row: it is generated from NEC rules by deriveRow() below and is
// marked `derived: true` so the UI can say so.
//
// NOTES:
// - FLA values are from NEC Table 430.250 (3-Phase AC Motors)
// - Wire sizing based on NEC 430.22 (125% of motor FLC)
// - Ground sizing per NEC Table 250.122. The MCP is an instantaneous-trip
//   device, so 250.122(D)(2) sizes the EGC from the maximum dual-element fuse
//   (175% FLA). The BGD source sheet listed smaller EGCs than that minimum on
//   15 rows (480 V: 30, 50, 60, 100, 150, 200, 250, 300, 400, 450, 500 HP;
//   240 V: 25, 30, 50, 75 HP); those were typos in the standard and are
//   corrected here to the NEC minimum (2026-09-09).
// - MCP (Motor Circuit Protector) frame sized to carry the FLA; the trip is
//   (The source table listed a 225 A frame for 125/150 HP at 480 V and 60/75 HP
//   at 240 V; no instantaneous-trip frame series carries 225 A, so those rows
//   read 250 A, the catalog frame above 150 A.)
//   set per NEC 430.52(C)(3)
// - LRA (Locked Rotor Amps) calculated as approximately 6× FLA
//   This is an estimate; actual LRA varies by motor design and
//   code letter. For precise LRA, refer to motor nameplate or
//   NEC Table 430.7(B) for code letter calculation.
// - Starter sizes are NEMA designations per NEC 430.83
// - SBKW = Stand-By Kilowatts (generator sizing allowance)
// - KVA = Apparent power (FLA × V × √3 / 1000)

import { BGDMotorData, WireSize, WIRE_SIZES_ORDERED, COMPANY_STANDARDS, CONDUIT_SIZES } from '../types';
import { getBaseAmpacity } from './necTable310_16';
import { getEGCSize } from './necTable250_122';
import { getThreePhaseFLA } from './necTable430';
import { getConduitArea, getConductorArea, getMaxFillPercent } from './necChapter9';
import { getMCPFrameSize, getInstantaneousTripEGCBasis } from '../calculations/mcpSizing';
import { getNEMAStarterSize } from '../calculations/controllerSizing';

/** A BGD table row; `derived` marks rows generated from NEC rules rather than the BGD source table. */
export interface BGDMotorRow extends BGDMotorData {
  /** True when the row is not in the BGD source table and was derived from NEC rules. */
  derived?: boolean;
}

export interface BGDParallelConfig {
  sets: number;
  wireSize: WireSize;
  groundSize: WireSize;
  conduitSize: string;
}

// 480V Three-Phase 60Hz Motors (BGD source table, 0.5-500 HP)
export const BGD_480V_3PH_MOTORS: BGDMotorRow[] = [
  { hp: 0.5, fla: 1.1, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 6.6, mcp: '7A', sbkw: 0.5, kva: 0.9 },
  { hp: 0.75, fla: 1.6, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 9.6, mcp: '7A', sbkw: 0.8, kva: 1.3 },
  { hp: 1, fla: 2.1, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 12.6, mcp: '7A', sbkw: 1, kva: 1.7 },
  { hp: 1.5, fla: 3, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 18, mcp: '7A', sbkw: 1.5, kva: 2.5 },
  { hp: 2, fla: 3.4, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 20.4, mcp: '7A', sbkw: 1.9, kva: 2.8 },
  { hp: 3, fla: 4.8, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 26.8, mcp: '7A', sbkw: 2.8, kva: 4.0 },
  { hp: 5, fla: 7.6, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 45.6, mcp: '15A', sbkw: 4.5, kva: 6.3 },
  { hp: 7.5, fla: 11, wire: -12, ground: -12, conduit: '1"', starter: 1, lra: 66, mcp: '15A', sbkw: 6.6, kva: 9.1 },
  { hp: 10, fla: 14, wire: -12, ground: -10, conduit: '1"', starter: 1, lra: 84, mcp: '30A', sbkw: 9, kva: 11.6 },
  { hp: 15, fla: 21, wire: -10, ground: -10, conduit: '1"', starter: 2, lra: 126, mcp: '30A', sbkw: 13, kva: 17.5 },
  { hp: 20, fla: 27, wire: -8, ground: -10, conduit: '1"', starter: 2, lra: 162, mcp: '50A', sbkw: 17, kva: 22.4 },
  { hp: 25, fla: 34, wire: -8, ground: -10, conduit: '1"', starter: 2, lra: 204, mcp: '50A', sbkw: 21, kva: 28.3 },
  { hp: 30, fla: 40, wire: -6, ground: -8, conduit: '1"', starter: 3, lra: 240, mcp: '50A', sbkw: 25, kva: 33.3 },
  { hp: 40, fla: 52, wire: -4, ground: -8, conduit: '1"', starter: 3, lra: 312, mcp: '100A', sbkw: 34, kva: 43.2 },
  { hp: 50, fla: 65, wire: -4, ground: -6, conduit: '1"', starter: 3, lra: 390, mcp: '100A', sbkw: 42, kva: 54.0 },
  { hp: 60, fla: 77, wire: -3, ground: -6, conduit: '1.25"', starter: 4, lra: 462, mcp: '100A', sbkw: 50, kva: 64.0 },
  { hp: 75, fla: 96, wire: -1, ground: -6, conduit: '1.5"', starter: 4, lra: 576, mcp: '150A', sbkw: 62, kva: 79.8 },
  { hp: 100, fla: 124, wire: 0.5, ground: -4, conduit: '2"', starter: 4, lra: 744, mcp: '150A', sbkw: 82, kva: 103.1 },
  { hp: 125, fla: 156, wire: 0.75, ground: -4, conduit: '2.5"', starter: 5, lra: 936, mcp: '250A', sbkw: 103, kva: 129.7 },
  { hp: 150, fla: 180, wire: 0.9, ground: -3, conduit: '2.5"', starter: 5, lra: 1080, mcp: '250A', sbkw: 123, kva: 149.6 },
  { hp: 200, fla: 240, wire: 350, ground: -2, conduit: '3"', starter: 5, lra: 1440, mcp: '400A', sbkw: 163, kva: 199.5 },
  { hp: 250, fla: 302, wire: 500, ground: -1, conduit: '3.5"', starter: 6, lra: 1812, mcp: '400A', sbkw: 203, kva: 251.1 },
  // Note: 300+ HP require parallel conductors - stored with special notation
  { hp: 300, fla: 361, wire: 0.9, ground: 0, conduit: '2.5"', starter: 6, lra: 2166, mcp: '400A', sbkw: 242, kva: 300.1 },  // (2) sets
  { hp: 350, fla: 414, wire: 300, ground: 0, conduit: '3"', starter: 6, lra: 2484, mcp: '600A', sbkw: 282, kva: 344.2 },      // (2) sets
  { hp: 400, fla: 477, wire: 350, ground: 0.5, conduit: '3"', starter: 6, lra: 2862, mcp: '600A', sbkw: 322, kva: 396.6 },      // (2) sets
  { hp: 450, fla: 515, wire: 400, ground: 0.5, conduit: '3"', starter: 7, lra: 3090, mcp: '800A', sbkw: 361, kva: 428.2 },      // (2) sets
  { hp: 500, fla: 590, wire: 500, ground: 0.75, conduit: '3.5"', starter: 7, lra: 3540, mcp: '800A', sbkw: 400, kva: 490.5 },  // (2) sets
];

// Parallel conductor requirements for 480V motors (BGD source table)
export const BGD_480V_PARALLEL: Record<number, BGDParallelConfig> = {
  300: { sets: 2, wireSize: 0.9, groundSize: 0, conduitSize: '2.5"' },     // (2) 4/0 per phase
  350: { sets: 2, wireSize: 300, groundSize: 0, conduitSize: '3"' },         // (2) 300 MCM per phase
  400: { sets: 2, wireSize: 350, groundSize: 0.5, conduitSize: '3"' },         // (2) 350 MCM per phase
  450: { sets: 2, wireSize: 400, groundSize: 0.5, conduitSize: '3"' },         // (2) 400 MCM per phase
  500: { sets: 2, wireSize: 500, groundSize: 0.75, conduitSize: '3.5"' },     // (2) 500 MCM per phase
};

// 240V Three-Phase 60Hz Motors - BGD source table rows (0.5-75 HP)
const BGD_240V_SOURCE_ROWS: BGDMotorRow[] = [
  { hp: 0.5, fla: 2.2, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 13, mcp: '7A', sbkw: 0.5, kva: 0.9 },
  { hp: 0.75, fla: 3.2, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 19, mcp: '7A', sbkw: 0.8, kva: 1.3 },
  { hp: 1, fla: 4.2, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 25, mcp: '7A', sbkw: 1, kva: 1.7 },
  { hp: 1.5, fla: 6, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 36, mcp: '15A', sbkw: 1.5, kva: 2.5 },
  { hp: 2, fla: 6.8, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 41, mcp: '15A', sbkw: 1.9, kva: 2.8 },
  { hp: 3, fla: 9.6, wire: -12, ground: -12, conduit: '0.75"', starter: 1, lra: 58, mcp: '15A', sbkw: 2.8, kva: 4.0 },
  { hp: 5, fla: 15.2, wire: -10, ground: -10, conduit: '1"', starter: 1, lra: 91, mcp: '30A', sbkw: 4.5, kva: 6.3 },
  { hp: 7.5, fla: 22, wire: -10, ground: -10, conduit: '1"', starter: 1, lra: 132, mcp: '50A', sbkw: 6.6, kva: 9.1 },
  { hp: 10, fla: 28, wire: -8, ground: -10, conduit: '1"', starter: 2, lra: 168, mcp: '50A', sbkw: 9, kva: 11.6 },
  { hp: 15, fla: 42, wire: -6, ground: -8, conduit: '1"', starter: 2, lra: 252, mcp: '100A', sbkw: 13, kva: 17.5 },
  { hp: 20, fla: 54, wire: -4, ground: -8, conduit: '1.25"', starter: 3, lra: 324, mcp: '100A', sbkw: 17, kva: 22.4 },
  { hp: 25, fla: 68, wire: -3, ground: -6, conduit: '1.5"', starter: 3, lra: 408, mcp: '100A', sbkw: 21, kva: 28.3 },
  { hp: 30, fla: 80, wire: -2, ground: -6, conduit: '1.5"', starter: 3, lra: 480, mcp: '100A', sbkw: 25, kva: 33.3 },
  { hp: 40, fla: 104, wire: 0, ground: -6, conduit: '2"', starter: 4, lra: 624, mcp: '150A', sbkw: 34, kva: 43.2 },
  { hp: 50, fla: 130, wire: 0.5, ground: -4, conduit: '2"', starter: 4, lra: 780, mcp: '150A', sbkw: 42, kva: 54.0 },
  { hp: 60, fla: 154, wire: 0.75, ground: -4, conduit: '2.5"', starter: 5, lra: 924, mcp: '250A', sbkw: 50, kva: 64.0 },
  { hp: 75, fla: 192, wire: 250, ground: -3, conduit: '3"', starter: 5, lra: 1152, mcp: '250A', sbkw: 62, kva: 80.0 },
];

// ---------------------------------------------------------------------------
// Derived rows (not in the BGD source table)
// ---------------------------------------------------------------------------

/** Largest single conductor before paralleling (BGD practice: 500 kcmil). */
const MAX_SINGLE_CONDUCTOR: WireSize = 500;
/** Raceway assumed for the derived conduit sizes (BGD water/wastewater practice). */
const DERIVED_CONDUIT_TYPE = 'PVC40';

/**
 * Smallest 75°C copper conductor whose Table 310.16 ampacity is at least the
 * required value (NEC 430.22: 125% FLA). Motor circuits are exempt from the
 * 240.4(D) small-conductor limits (240.4(G)); the company minimum #12 applies.
 * Above 500 kcmil, parallel sets of 1/0 AWG or larger (310.10(G)), fewest
 * sets first.
 */
function selectDerivedConductor(requiredAmpacity: number): { wire: WireSize; sets: number } {
  for (const size of WIRE_SIZES_ORDERED) {
    if (size < COMPANY_STANDARDS.minConductorSize) continue;
    if (size > MAX_SINGLE_CONDUCTOR) break;
    if (getBaseAmpacity(size, 'copper', 75) >= requiredAmpacity) return { wire: size, sets: 1 };
  }
  for (let sets = 2; sets <= 6; sets++) {
    for (const size of WIRE_SIZES_ORDERED) {
      if (size < 0 || size > MAX_SINGLE_CONDUCTOR) continue;
      if (getBaseAmpacity(size, 'copper', 75) * sets >= requiredAmpacity) return { wire: size, sets };
    }
  }
  throw new Error(`No BGD conductor configuration for ${requiredAmpacity} A`);
}

/** Smallest trade size (company minimum 1") holding 3 phase conductors + EGC (THHN) at 40% fill. */
function derivedConduitFor(wire: WireSize, ground: WireSize): string {
  const area = 3 * getConductorArea(wire, 'standard', 'THHN') + getConductorArea(ground, 'standard', 'THHN');
  const maxFill = getMaxFillPercent(4) / 100;
  for (const size of CONDUIT_SIZES) {
    if (size < COMPANY_STANDARDS.minConduitSize) continue;
    if (getConduitArea(size, DERIVED_CONDUIT_TYPE) * maxFill >= area) return `${size}"`;
  }
  return `${CONDUIT_SIZES[CONDUIT_SIZES.length - 1]}"`;
}

/**
 * Generate a motor table row from NEC rules for a voltage/HP the BGD source
 * table does not carry:
 *  - FLA: Table 430.250 (230 V column for 240 V systems, 208 V column)
 *  - conductor: 75°C Cu, ampacity ≥ 125% FLA (430.22), company minimum #12
 *  - EGC: Table 250.122 at the maximum dual-element fuse rating (250.122(D)(2))
 *  - NEMA starter: NEMA ICS 2 at 230 V / 200 V, not below the BGD minimum size 1
 *  - MCP: smallest frame ≥ FLA (trip set per 430.52(C)(3))
 *  - LRA ≈ 6 × FLA; SBKW from the 480 V row; kVA = FLA × V × √3 / 1000
 */
function deriveRow(hp: number, voltage: 240 | 208): { row: BGDMotorRow; parallel?: BGDParallelConfig } {
  const fla = getThreePhaseFLA(hp, voltage);
  if (fla === undefined) {
    throw new Error(`No NEC Table 430.250 value for ${hp} HP at ${voltage} V`);
  }
  const { wire, sets } = selectDerivedConductor(fla * 1.25);
  const ground = getEGCSize(getInstantaneousTripEGCBasis(fla), 'copper');
  const conduit = derivedConduitFor(wire, ground);
  const frame = getMCPFrameSize(fla);
  const nemaSize = parseInt(getNEMAStarterSize(hp, voltage === 240 ? 230 : 200), 10);
  const starter = Math.max(1, Number.isFinite(nemaSize) ? nemaSize : 1);
  const sbkw = BGD_480V_3PH_MOTORS.find(m => m.hp === hp)?.sbkw ?? Math.round(hp * 0.746 * 10) / 10;
  const row: BGDMotorRow = {
    hp,
    fla,
    wire,
    ground,
    conduit,
    starter,
    lra: Math.round(fla * 6),
    // The table's own notation, like the BGD-sourced rows ('7A');
    // displays space it with formatRatingText.
    mcp: frame === null ? '—' : `${frame}A`,
    sbkw,
    kva: Math.round((fla * voltage * Math.sqrt(3)) / 1000 * 10) / 10,
    derived: true,
  };
  const parallel = sets > 1 ? { sets, wireSize: wire, groundSize: ground, conduitSize: conduit } : undefined;
  return { row, parallel };
}

/** HP ratings above the 240 V source table (Table 430.250 carries 230 V values to 200 HP). */
const DERIVED_HP_240V = [100, 125, 150, 200];
/** The 208 V table is entirely derived; 208 V is not in the BGD source. */
const DERIVED_HP_208V = [0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 125, 150, 200];

const derived240 = DERIVED_HP_240V.map(hp => deriveRow(hp, 240));
const derived208 = DERIVED_HP_208V.map(hp => deriveRow(hp, 208));

// 240V Three-Phase 60Hz Motors: source rows 0.5-75 HP plus derived rows above.
// The table is limited to 200 HP (Table 430.250 has no 230 V value above that).
export const BGD_240V_3PH_MOTORS: BGDMotorRow[] = [
  ...BGD_240V_SOURCE_ROWS,
  ...derived240.map(d => d.row),
];

// Parallel conductor requirements for 240V motors (derived rows only)
export const BGD_240V_PARALLEL: Record<number, BGDParallelConfig> = Object.fromEntries(
  derived240.filter(d => d.parallel).map(d => [d.row.hp, d.parallel!])
);

// 208V Three-Phase 60Hz Motors (all rows derived from NEC; 208 V is not in the BGD source)
export const BGD_208V_3PH_MOTORS: BGDMotorRow[] = derived208.map(d => d.row);

// Parallel conductor requirements for 208V motors (derived rows only)
export const BGD_208V_PARALLEL: Record<number, BGDParallelConfig> = Object.fromEntries(
  derived208.filter(d => d.parallel).map(d => [d.row.hp, d.parallel!])
);

// Valid HP values
export const VALID_HP_480V: number[] = [0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 125, 150, 200, 250, 300, 350, 400, 450, 500];
export const VALID_HP_240V: number[] = [0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 125, 150, 200];
export const VALID_HP_208V: number[] = [0.5, 0.75, 1, 1.5, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 125, 150, 200];

/**
 * Get BGD motor data for 480V three-phase motor
 * @param hp - Motor horsepower
 * @returns Motor data or undefined if not found
 */
export function getBGD480VMotor(hp: number): BGDMotorRow | undefined {
  return BGD_480V_3PH_MOTORS.find(m => m.hp === hp);
}

/**
 * Get BGD motor data for 240V three-phase motor
 * @param hp - Motor horsepower
 * @returns Motor data or undefined if not found
 */
export function getBGD240VMotor(hp: number): BGDMotorRow | undefined {
  return BGD_240V_3PH_MOTORS.find(m => m.hp === hp);
}

/**
 * Get BGD motor data for 208V three-phase motor (derived from NEC)
 * @param hp - Motor horsepower
 * @returns Motor data or undefined if not found
 */
export function getBGD208VMotor(hp: number): BGDMotorRow | undefined {
  return BGD_208V_3PH_MOTORS.find(m => m.hp === hp);
}

/**
 * Smallest listed BGD HP at or above the requested HP (the BGD standard says
 * to round UP to the next listed size), or undefined above the table maximum.
 */
/**
 * BGD row for a motor: the exact row, else the next listed horsepower up
 * (a 9.4 HP nameplate sizes as 10 HP — NEC 430.6(A)(1) practice and what the
 * sets do), undefined above the table.
 */
export function getBGDMotorRowForHp(hp: number, voltage: 480 | 240 | 208): BGDMotorRow | undefined {
  const listed = getBGDListedHP(hp, voltage);
  if (listed === undefined) return undefined;
  return voltage === 480 ? getBGD480VMotor(listed) : voltage === 240 ? getBGD240VMotor(listed) : getBGD208VMotor(listed);
}

export function getBGDListedHP(hp: number, voltage: 480 | 240 | 208): number | undefined {
  const list = voltage === 480 ? VALID_HP_480V : voltage === 240 ? VALID_HP_240V : VALID_HP_208V;
  return list.find(listed => listed >= hp);
}

/**
 * Check if motor requires parallel conductors
 * @param hp - Motor horsepower
 * @param voltage - Voltage (480, 240, or 208) - defaults to 480V
 * @returns Parallel configuration or undefined
 */
export function getParallelConfig(hp: number, voltage: 480 | 240 | 208 = 480): { sets: number; wireSize: number; groundSize: number; conduitSize: string } | undefined {
  if (voltage === 480) {
    return BGD_480V_PARALLEL[hp];
  } else if (voltage === 240) {
    return BGD_240V_PARALLEL[hp];
  } else {
    return BGD_208V_PARALLEL[hp];
  }
}

/**
 * Check if HP is valid for BGD tables at given voltage
 * @param hp - Motor horsepower
 * @param voltage - Voltage (480, 240, or 208)
 * @returns True if valid
 */
export function isValidBGDMotor(hp: number, voltage: 480 | 240 | 208): boolean {
  if (voltage === 480) {
    return VALID_HP_480V.includes(hp);
  } else if (voltage === 240) {
    return VALID_HP_240V.includes(hp);
  }
  return VALID_HP_208V.includes(hp);
}

export const BGD_MOTOR_SIZING_REF = {
  table: 'BGD Motor Sizing Standard',
  description: 'BGD Engineering standard motor circuit sizing for 480V (0.5-500 HP) and 240V (0.5-75 HP) three-phase motors. Values meet or exceed NEC requirements. Other rows are derived from NEC rules.',
};
